import { useEffect, useState } from 'react';
import { ListItemButton, Typography } from '@mui/material';
import { getClassSectionList } from 'apis/main';
import MuiAutocomplete from 'components/MuiAutocomplete';
import useClassSection from 'store/section/useClassSection';

interface IClassSection {
  class_section_id: number;
  college_id?: number;
  class_section_year: number;
  class_section_semester: number;
  class_section_subject: string;
  class_section_grade: number;
  class_section_name: string;
  class_section_teacher_info?: string;
  class_section_create_id?: number;
  class_section_create_date?: string;
}

interface IOption extends IClassSection {
  displayName: string;
  college_id?: number;
  student_count?: number;
}

const ClassSectionList = ({ user_id }: { user_id: number }) => {
  const [options, setOptions] = useState<IOption[]>([]);
  const { onSelectedClassSection } = useClassSection();

  // 전체 학생 선택 옵션
  const allStudentsOption: IOption = {
    class_section_id: -1, // 특별한 ID 부여
    displayName: 'All Students',
    class_section_year: 0,
    class_section_semester: 0,
    class_section_subject: '',
    class_section_grade: 0,
    class_section_name: '',
  };

  // API 응답을 가공하는 함수
  const processApiResponse = (response: any) => {
    const apiOptions = response.data.class_section_list || [];
    // 전체 학생 선택 옵션을 맨 앞에 추가
    return [allStudentsOption, ...apiOptions];
  };

  // Option 항목에 표시될 텍스트 포맷 지정
  const optionLabel = (option: IOption) => {
    if (!option) return '';
    // displayName이 없는 경우 생성
    if (!option.displayName) {
      option.displayName = `${option.class_section_year}년 ${option.class_section_semester}학기 ${option.class_section_subject} ${option.class_section_grade}-${option.class_section_name}`;
    }
    return option.displayName;
  };

  // 옵션 항목으로 렌더링될 컴포넌트
  const Option = ({
    class_section_year,
    class_section_semester,
    class_section_subject,
    class_section_grade,
    class_section_name,
    ...props
  }: IOption & Record<string, any>) => {
    return (
      <ListItemButton {...props} sx={{ gap: 0.5 }}>
        <Typography variant="caption">
          {class_section_year}년 {class_section_semester}학기
        </Typography>
        <Typography variant="subtitle2" color={'#000000B2'}>
          {class_section_subject}
        </Typography>
        <Typography variant="subtitle2" color={'#000000B2'}>
          {class_section_grade}-{class_section_name}
        </Typography>
      </ListItemButton>
    );
  };

  // 선택된 분반을 상태로 저장
  const handleSectionChange = (section: IOption | null) => {
    onSelectedClassSection(section as IOption);
  };

  return (
    <MuiAutocomplete
      variant="standard"
      valueKey="class_section_id"
      listKey="class_section_list"
      placeholder="選擇班級"
      noOptionsText="no class sections available"
      getOptionLabel={optionLabel}
      renderOption={(props, option) => {
        // 전체 학생 선택 옵션일 경우 다르게 렌더링
        if (option.class_section_id === -1) {
          return (
            <ListItemButton {...props}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'normal' }}>
                {option.displayName}
              </Typography>
            </ListItemButton>
          );
        }
        return <Option {...props} {...option} />;
      }}
      getApi={async (request: any) => {
        const response = await getClassSectionList({
          user_id,
          size: 5,
          page: request.page,
        });
        response.data.class_section_list = processApiResponse(response);
        return response;
      }}
      onChange={handleSectionChange}
    />
  );
};

export default ClassSectionList;
